import { useEffect } from "react";
import { navigate, navigateBack } from "utilities/navigate";
import { getBridge } from "utilities/bridge";
import { reconnectFirestore } from "utilities/firebase";
import localObject from "utilities/localObject";
import { ATTRIBUTION_KEY } from "appConfig";
import * as Sentry from "@sentry/gatsby";
import { useIsClient } from "@/hooks/useIsClient";

import { useWalletStore } from "@/features/wallet/stores/walletStore";
import { walletService } from "@/features/wallet/services/wallet";
import { useContentCardsStore } from "@/features/braze/stores/contentCardsStore";
import { fromBridgeCard } from "@/features/braze/utils/normalise";
import { titleCase } from "title-case";
import {
  selectOverlayOpen,
  useShellStore,
} from "@/features/shell/stores/shellStore";
import { toast } from "@picklebet/ui";
import { showCustomIAM } from "@/features/braze/iam/ShowCustomIAM";
import { useLocation } from "@/hooks/useLocation";
import {
  loginWithBiometrics,
  enrollBiometrics,
  deleteBiometrics,
} from "features/auth/services/biometrics";
import { signInWithSessionObject } from "@/features/auth/services/session";
import { BET_STOP_MODAL_ID_LOGOUT } from "@/features/auth/components/BetStopModalLogout";
import { telemetry } from "@/utilities/telemetry";
import { parseError } from "utilities/errorsParser";

/**
 * Handles incoming events from native apps via the messaging bridge.
 * This component subscribes to app-to-web events and processes them accordingly.
 */
export const BridgeEventHandler = () => {
  const isClient = useIsClient();
  const { pathname } = useLocation();
  const setAppleDepositStatus = useWalletStore(
    (state) => state.setAppleDepositStatus,
  );
  const setAppleDepositError = useWalletStore(
    (state) => state.setAppleDepositError,
  );
  const setGoogleDepositStatus = useWalletStore(
    (state) => state.setGoogleDepositStatus,
  );
  const setGoogleDepositError = useWalletStore(
    (state) => state.setGoogleDepositError,
  );
  const setApplePayAvailable = useWalletStore(
    (state) => state.setApplePayAvailable,
  );
  const setGooglePayAvailable = useWalletStore(
    (state) => state.setGooglePayAvailable,
  );
  const setBiometricAvailability = useShellStore(
    (state) => state.setBiometricAvailability,
  );
  const setIsBiometricProcessing = useShellStore(
    (state) => state.setIsBiometricProcessing,
  );
  const closeOverlays = useShellStore((state) => state.closeOverlays);
  const setModal = useShellStore((state) => state.setModal);
  const setIsDeeplinkOverlayActive = useShellStore(
    (state) => state.setIsDeeplinkOverlayActive,
  );
  const setKeyboardOpen = useShellStore((state) => state.setKeyboardOpen);

  useEffect(() => {
    if (!isClient) return;

    const bridge = getBridge();
    const unsubscribers: (() => void)[] = [];

    // Track deeplink overlay state to suppress auto-focus while the overlay is visible
    unsubscribers.push(
      bridge.on.deeplinkOverlayActive((isActive) => {
        setIsDeeplinkOverlayActive(isActive);
      }),
    );

    unsubscribers.push(bridge.on.keyboardVisible(setKeyboardOpen));

    // Returning from the background is when the Firestore stream goes stale
    unsubscribers.push(
      bridge.on.appState((status) => {
        if (status !== "active") return;
        reconnectFirestore().catch((error) => Sentry.captureException(error));
      }),
    );

    // Navigate to a URL
    unsubscribers.push(
      bridge.on.navigate((payload) => {
        setModal(null);
        closeOverlays();
        // wait for setModal & closeOverlays to run first
        requestAnimationFrame(() => {
          navigate(payload.url);
        });
      }),
    );

    // Handle back navigation
    unsubscribers.push(
      bridge.on.back(() => {
        // Need getState() here to read the overlays at event time, not render time
        if (selectOverlayOpen(useShellStore.getState())) {
          closeOverlays();
        } else {
          navigateBack();
        }
      }),
    );

    // Set attribution data
    unsubscribers.push(
      bridge.on.attribution((payload) => {
        let [attribution] = localObject.getValue<Record<string, any>>(
          ATTRIBUTION_KEY,
          {},
        );

        // in case browser did not return valid data make sure we fall back to default value
        if (typeof attribution === "undefined" || attribution === null)
          attribution = {};

        Object.entries(payload).forEach(([key, value]) => {
          if (value !== undefined) {
            attribution[key] = value;
          }
        });

        localObject.setValue(ATTRIBUTION_KEY, attribution);
      }),
    );

    // Handle Apple Pay token
    unsubscribers.push(
      bridge.on.applePayToken(async (payload) => {
        setAppleDepositError(null);
        setAppleDepositStatus("processing");

        const result = await walletService.merchantWarriorDeposit({
          digitalWalletToken: payload.digitalWalletToken,
          amount: payload.amount,
          digitalWalletType: "Apple",
          panSuffix: payload.panSuffix,
        });

        const isSuccess = result.ok && result.data === "success";

        getBridge().send.applePayFinish({
          outcome: isSuccess ? "success" : "failure",
        });

        if (isSuccess) {
          setAppleDepositStatus("success");
          return;
        }

        if (!result.ok) {
          setAppleDepositError(parseError(result.error));
        }

        setAppleDepositStatus("ready");
      }),
    );

    // Apple Pay availability
    unsubscribers.push(
      bridge.on.applePayAvailable((isAvailable) => {
        setApplePayAvailable(isAvailable);
      }),
    );

    // Google Pay availability
    unsubscribers.push(
      bridge.on.googlePayAvailable((isAvailable) => {
        setGooglePayAvailable(isAvailable);
      }),
    );

    // Log to Sentry
    unsubscribers.push(
      bridge.on.sentryLog((payload) => {
        try {
          Sentry.captureEvent({
            message: payload.message,
            level: "error",
            extra: payload.extra,
          });
        } catch (e) {
          console.log(e);
        }
      }),
    );

    // Deprecated: Google Pay token (pre v13.0.x)
    unsubscribers.push(
      bridge.on.googlePayToken(async (payload) => {
        setGoogleDepositError(null);

        if (payload.error) {
          toast({
            description: titleCase(payload.error.message),
            variant: "danger",
          });
          setGoogleDepositStatus("ready");
        } else {
          setGoogleDepositStatus("processing");

          const result = await walletService.merchantWarriorDeposit({
            digitalWalletToken: JSON.parse(payload.digitalWalletToken),
            amount: payload.amount,
            digitalWalletType: "Google",
            panSuffix: "",
          });

          if (!result.ok) {
            setGoogleDepositError(parseError(result.error));
          } else if (result.data === "success") {
            setGoogleDepositStatus("success");
            return;
          }

          setGoogleDepositStatus("ready");
        }
      }),
    );

    // Google Pay token v2 (v13.0.x+)
    unsubscribers.push(
      bridge.on.googlePayTokenV2(async (payload) => {
        setGoogleDepositError(null);

        if (payload.error) {
          toast({
            description: titleCase(payload.error.message),
            variant: "danger",
          });
          setGoogleDepositStatus("ready");
        } else {
          setGoogleDepositStatus("processing");

          const result = await walletService.merchantWarriorDeposit({
            digitalWalletToken: payload.digitalWalletToken,
            amount: payload.amount,
            digitalWalletType: "Google",
            panSuffix: payload.panSuffix,
          });

          if (!result.ok) {
            setGoogleDepositError(parseError(result.error));
          } else if (result.data === "success") {
            setGoogleDepositStatus("success");
            return;
          }

          setGoogleDepositStatus("ready");
        }
      }),
    );

    // Biometrics
    unsubscribers.push(
      bridge.on.biometricAvailability((payload) => {
        const { biometryType: type } = payload;

        setBiometricAvailability({
          ...payload,
          biometryType:
            type === "FaceID" || type === "TouchID" || type === "Biometrics"
              ? type
              : null,
        });
      }),
    );

    unsubscribers.push(
      bridge.on.biometricKeyToEnroll(async (payload) => {
        const { success, publicKey, error, token, source } = payload;
        const { biometryType } =
          useShellStore.getState().deviceBiometricAvailability;

        if (!success || !publicKey) {
          telemetry.biometricsEnabled({
            outcome: "failure",
            source,
            biometryType: biometryType ?? undefined,
            failureStep: "key_generation",
            errorMessage: error ?? undefined,
          });
          setIsBiometricProcessing(false);
          return;
        }

        const result = await enrollBiometrics(publicKey, token);

        if (!result.ok) {
          telemetry.biometricsEnabled({
            outcome: "failure",
            source,
            biometryType: biometryType ?? undefined,
            failureStep: "api_enroll",
            errorMessage: result.error.message,
          });
          // api failed to enroll - only need to remove keys from the device
          bridge.send.biometricReset({ token, skipApi: true });
          return;
        }

        telemetry.biometricsEnabled({
          outcome: "success",
          source,
          biometryType: biometryType ?? undefined,
        });
        setBiometricAvailability({ enrolled: true });
        setIsBiometricProcessing(false);
      }),
    );

    unsubscribers.push(
      bridge.on.removeBiometricKey(async (payload) => {
        const { token, skipApi } = payload;

        if (skipApi) {
          setBiometricAvailability({ enrolled: false });
          setIsBiometricProcessing(false);
          return;
        }

        // even if native key removal failed, still call api to delete keys
        const result = await deleteBiometrics(token);

        if (!result.ok) {
          telemetry.biometricsDisabled({
            outcome: "failure",
            errorMessage: result.error.message,
          });
          setIsBiometricProcessing(false);
          return;
        }

        telemetry.biometricsDisabled({ outcome: "success" });
        setBiometricAvailability({ enrolled: false });
        setIsBiometricProcessing(false);
      }),
    );

    unsubscribers.push(
      bridge.on.verifySignature(async (payload) => {
        if (!payload.success || !payload.signature) {
          // At this point, the key still exists so enrolled=true, but signing always
          // fails. We should delete the local key so the toggle turns off and
          // useBiometricSync can clean up the server-side enrollment after login.
          if (payload.errorCode === "KEY_PERMANENTLY_INVALIDATED") {
            bridge.send.biometricReset({ token: "", skipApi: true });
          }

          telemetry.loginAttempt({
            outcome: "failure",
            source: "biometrics",
            biometricFailureStep: "signature",
            errorMessage: payload.error ?? undefined,
          });
          setIsBiometricProcessing(false);
          setModal("signIn");
          return;
        }

        const result = await loginWithBiometrics(
          payload.challengeId,
          payload.signature,
        );

        if (!result.ok) {
          if (result.error.messageTemplate === "user.closed.nserMatch") {
            setModal(BET_STOP_MODAL_ID_LOGOUT);
          } else {
            setModal("signIn");
          }

          telemetry.loginAttempt({
            outcome: "failure",
            source: "biometrics",
            biometricFailureStep: "api_login",
            errorMessage: result.error.message,
            errorMessageTemplate: result.error.messageTemplate,
          });
          setIsBiometricProcessing(false);
          return;
        }

        try {
          await signInWithSessionObject(result.data);
          telemetry.loginAttempt({ outcome: "success", source: "biometrics" });
          setModal(null);

          const permissions = result.data.permissions || {};

          if (permissions?.reopenAccount === "GRANTED") {
            navigate("/account/reopen");
          }
        } catch (error) {
          telemetry.loginAttempt({
            outcome: "failure",
            source: "biometrics",
            biometricFailureStep: "session_ingestion",
            errorMessage: (error as Error).message,
          });
        } finally {
          setIsBiometricProcessing(false);
        }
      }),
    );

    // Custom in-app message
    unsubscribers.push(
      bridge.on.customIAM((payload) => {
        showCustomIAM(payload);
      }),
    );

    // Braze content cards
    unsubscribers.push(
      bridge.on.brazeContentCards((cards) => {
        useContentCardsStore.getState().setCards(cards.map(fromBridgeCard));
      }),
    );

    // Let the apps know we are ready for messages
    bridge.send.webReady();

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [
    setAppleDepositStatus,
    setAppleDepositError,
    setGoogleDepositStatus,
    setApplePayAvailable,
    setGooglePayAvailable,
    closeOverlays,
    setModal,
    setIsDeeplinkOverlayActive,
    setKeyboardOpen,
    setBiometricAvailability,
    setIsBiometricProcessing,
    isClient,
  ]);

  useEffect(() => {
    if (!isClient) {
      return;
    }

    getBridge().send.routeChanged({ pathname });
  }, [pathname, isClient]);

  return null;
};
